package nl.volleyplanner.web

import kotlinx.coroutines.*
import kotlin.concurrent.atomics.AtomicLong
import kotlin.concurrent.atomics.ExperimentalAtomicApi
import kotlin.concurrent.atomics.fetchAndIncrement


@OptIn(ExperimentalAtomicApi::class)
object CoroutineManager {
    private val logger = Logger.getLogger("CoroutineManager")
    private val applicationJob = SupervisorJob()

    private val coroutineIds = AtomicLong(1000L)

    val coroutineExceptionHandler = CoroutineExceptionHandler { context, throwable ->
        val coroutineName = context[CoroutineName.Key]?.name ?: "Unnamed Coroutine"

        logger.throwable("[$coroutineName] Caught exception: ${throwable.message}", throwable)
    }

    val APPLICATION_SCOPE =
        CoroutineScope(Dispatchers.Default + applicationJob + CoroutineName("ApplicationScope") + coroutineExceptionHandler)

    /**
     * Use this scope for any job that should run in the background across multiple pages
     */
    val DISPATCHER_SCOPE =
        CoroutineScope(Dispatchers.Default + SupervisorJob(applicationJob) + CoroutineName("DispatcherScope") + coroutineExceptionHandler)

    private var _CONTROLLER_SCOPE: CoroutineScope? = null
    private var _RENDER_SCOPE: CoroutineScope? = null

    /**
     * Use controller scope for any job that should run in a controller that might be valid across multiple renders
     * Gets cancelled and recreated when switching to a new controller
     */
    val CONTROLLER_SCOPE get() = _CONTROLLER_SCOPE ?: newControllerScope()

    /**
     * Use render scope for any job that can be cancelled when refreshing pages
     * Do not use this for background processes that post data, as these might not be completed
     */
    val RENDER_SCOPE get() = _RENDER_SCOPE ?: newRenderScope()

    fun newControllerScope() : CoroutineScope {
        val coroutineName = CoroutineName("ControllerScope-${coroutineIds.fetchAndIncrement()}")
        logger.debug("Creating new controller scope: $coroutineName")
        _CONTROLLER_SCOPE?.cancel()
        return CoroutineScope(Dispatchers.Default + SupervisorJob(applicationJob) + coroutineName + coroutineExceptionHandler)
            .also { _CONTROLLER_SCOPE = it }
    }

    fun newRenderScope() : CoroutineScope {
        val coroutineName = CoroutineName("RenderScope-${coroutineIds.fetchAndIncrement()}")
        logger.debug("Creating new render scope: $coroutineName")
        _RENDER_SCOPE?.cancel()
        return CoroutineScope(Dispatchers.Default + SupervisorJob(applicationJob) + coroutineName + coroutineExceptionHandler)
            .also { _RENDER_SCOPE = it }
    }

    fun newServiceScope(name: String) : CoroutineScope {
        logger.debug("Creating new service scope: $name")
        return CoroutineScope(Dispatchers.Default + SupervisorJob(applicationJob) + CoroutineName(name) + coroutineExceptionHandler)
    }

    fun createChildScope(parent: CoroutineScope) : CoroutineScope {
        return CoroutineScope(parent.coroutineContext + SupervisorJob(parent.coroutineContext[Job]))
    }

    fun shutdown() {
        logger.error { "Shutting down application" }
        APPLICATION_SCOPE.cancel()
    }
}